import { useTranslation } from "react-i18next"
import { useAppSelector } from "store/index"
import { AppSelectors } from "store/slices/appSlice"
import { ElixirModel, ElixirType } from "store/apis/elixirsAPI/values"
import {
  IDarkActiveElixir,
  IDarkElixirTemplate
} from "store/apis/darkElixirsAPI/dark-elixirs.types"
import {
  IActiveElixir,
  IActivePremiumElixir,
  IActivePremiumElixirElixir,
  IElixir
} from "store/apis/elixirsAPI/types"
import { useModal } from "context/ModalsContext"
import { useElixir } from "hooks/useElixir"
import { useHappyHour } from "hooks/useHappyHour"
import { inventoryItemImgs } from "helpers/elixirImgs"
import { formatTimeRemaining } from "helpers/formatTime.ts"
import { goldenClock } from "assets/icons"
import OutlinedText from "../outlinedText"
import "./index.scss"

const LeftSideMenu = () => {
  const { activeElixirs, activatedPremiumElixirs, activatedDarkElixirs } =
    useElixir()
  const isUnder = useAppSelector(AppSelectors.selectIsUnder())

  const items: Array<IActiveElixir | IActivePremiumElixir | IDarkActiveElixir> =
    isUnder
      ? activatedDarkElixirs
      : [
          ...activeElixirs.filter((e) => e.elixir.type === ElixirType.SPEED),
          ...activatedPremiumElixirs
        ]

  return (
    <div className="left-side-menu">
      {!isUnder && <HappyHourItem />}
      <div className="left-side-menu__items">
        {items.map((elixir) => (
          <Item
            key={elixir?.elixir._id}
            elixirId={elixir?.elixir._id}
            elixir={elixir?.elixir}
            isDark={isUnder}
          />
        ))}
      </div>
    </div>
  )
}

const HappyHourItem = () => {
  const { t } = useTranslation()
  const { isHappyHour, timeRemaining } = useHappyHour()
  const { openModal } = useModal()

  const handleClick = () => {
    openModal({ type: "happyHourDetails" })
  }

  return isHappyHour ? (
    <div className="hover-effect" onClick={handleClick}>
      <div className={"left-side-menu__item"}>
        <img src={goldenClock} alt={"happy-hour"} />
      </div>
      <OutlinedText>
        {formatTimeRemaining(timeRemaining, t)?.split(" ")[0]}
      </OutlinedText>
    </div>
  ) : null
}

const Item = ({
  elixirId,
  elixir,
  isDark
}: {
  elixirId: string
  elixir: IElixir | IDarkElixirTemplate | IActivePremiumElixirElixir
  isDark?: boolean
}) => {
  const { t } = useTranslation()
  const { timeRemaining } = useElixir()
  const { openModal } = useModal()

  const time = formatTimeRemaining(timeRemaining[elixirId], t)?.split(" ")[0]

  const handleClick = () => {
    openModal({
      type: "elixirDetails",
      params: {
        selectedElixir: isDark
          ? { ...elixir, elixirModel: ElixirModel.DARK }
          : elixir
      }
    })
  }
  const subType =
    !isDark && "subType" in elixir ? (elixir.subType as string) : ""
  const fallback = inventoryItemImgs[elixir?.type as ElixirType]?.[subType]
  const src: string | undefined =
    elixir?.imgSrc ?? (typeof fallback === "string" ? fallback : undefined)

  return elixir || time ? (
    <div>
      <div
        className="left-side-menu__item hover-effect left-side-menu__item--elixir"
        onClick={handleClick}
      >
        {src ? <img src={src} alt="" /> : <div className="img-placeholder" />}
      </div>
      {time ? <OutlinedText>{time}</OutlinedText> : null}
    </div>
  ) : null
}

export default LeftSideMenu
